import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import ArrowLeft from '@mui/icons-material/ArrowBackIosNew';
import ArrowRight from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CircularProgress from '@mui/material/CircularProgress';
import * as d3 from 'd3';
import { queryData } from "../../utils/api";

const colors = {
  blues: ['#eff3ff', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c', '#053e78'],
  noValue: '#dcdcdc'
};

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const margin = { top: 20, left: 200, right: 10, bottom: 20 };
const cellHeight = 20;
const cellPad = 1;
const panAmount = 100;

const quickRangeLabels = {
  '-2d': 'last 2 days',
  '-7d': 'last 7 days',
  '-30d': 'last 30 days',
  '-90d': 'last 90 days',
  '-1y': 'last 1 year',
  'showAll': 'show all'
};

function getColorForValue(count, maxCount, deviceType, timeWindow) {
  if (count === 0) return colors.noValue;
  
  const hourlyMax = deviceType === 'Sap Flow Sensor' ? 9 : 38;
  const maxExpected = timeWindow === 'daily' ? hourlyMax * 24 : hourlyMax;
  const ratio = count / maxExpected;
  const index = Math.min(6, Math.floor(ratio * 7));
  return colors.blues[index];
}

function formatTooltipDate(date, timeWindow, timezone = 'UTC') {
  const options = {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  };
  
  const dateStr = date.toLocaleString(undefined, options);
  return `${dateStr} (${timeWindow})`;
}

function drawTimeline(container, data, width, onCellClick, timeWindow, deviceTypes, locationName, allDeviceNames, timezone, deviceIdMap, onLabelClick) {
  d3.select(container).selectAll('*').remove();
  
  const yLabels = allDeviceNames || Object.keys(data);
  if (yLabels.length === 0) return;
  
  const chartData = [];
  
  let minTime = Infinity;
  let maxTime = -Infinity;
  
  yLabels.forEach(label => {
    const deviceData = data[label] || [];
    deviceData.forEach(record => {
      const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
      const ts = new Date(timestampStr).getTime();
      minTime = Math.min(minTime, ts);
      maxTime = Math.max(maxTime, ts);
    });
  });
  
  if (!isFinite(minTime) || !isFinite(maxTime)) {
    const now = Date.now();
    maxTime = now;
    minTime = now - (2 * DAY);
  }
  
  const cellUnit = timeWindow === 'daily' ? DAY : HOUR;
  const bucketStart = Math.floor(minTime / cellUnit) * cellUnit;
  const now = Date.now();
  const bucketEnd = Math.ceil(now / cellUnit) * cellUnit;
  
  yLabels.forEach(label => {
    const deviceData = data[label] || [];
    const bucketMap = {};
    
    let deviceId = '';
    if (deviceData.length > 0 && deviceData[0].meta) {
      deviceId = deviceData[0].meta.deviceId || '';
    }
    
    deviceData.forEach(record => {
      const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
      const timestamp = new Date(timestampStr).getTime();
      const bucketTime = Math.floor(timestamp / cellUnit) * cellUnit;
      bucketMap[bucketTime] = record;
      if (!deviceId && record.meta) {
        deviceId = record.meta.deviceId || '';
      }
    });
    
    for (let t = bucketStart; t <= bucketEnd; t += cellUnit) {
      if (bucketMap[t]) {
        chartData.push({ ...bucketMap[t], row: label });
      } else {
        chartData.push({
          timestamp: new Date(t).toISOString(),
          end: new Date(t + cellUnit).toISOString(),
          value: 0,
          meta: { deviceId: deviceId },
          row: label
        });
      }
    }
  });
  
  if (chartData.length === 0) return;
  
  const height = yLabels.length * cellHeight;
  const svgHeight = height + margin.top + margin.bottom;
  
  const sorted = [...chartData].sort((a, b) => {
    const aStr = a.timestamp.endsWith('Z') ? a.timestamp : a.timestamp + 'Z';
    const bStr = b.timestamp.endsWith('Z') ? b.timestamp : b.timestamp + 'Z';
    return new Date(aStr) - new Date(bStr);
  });
  const xDomain = [
    new Date(sorted[0].timestamp.endsWith('Z') ? sorted[0].timestamp : sorted[0].timestamp + 'Z'),
    new Date((sorted[sorted.length - 1].end || sorted[sorted.length - 1].timestamp).endsWith('Z') 
      ? (sorted[sorted.length - 1].end || sorted[sorted.length - 1].timestamp)
      : (sorted[sorted.length - 1].end || sorted[sorted.length - 1].timestamp) + 'Z')
  ];
  
  const x = d3.scaleTime()
    .domain(xDomain)
    .range([0, width]);
  
  const y = d3.scaleBand()
    .domain(yLabels)
    .range([0, height])
    .padding(0);
  
  const svg = d3.select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', svgHeight)
    .attr('cursor', 'grab')
    .style('display', 'block')
    .style('overflow', 'visible');
  
  const axisFormat = (date) => {
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone
    };
    return date.toLocaleString(undefined, options);
  };
  
  const xAxis = d3.axisTop(x).tickFormat(axisFormat);
  const gX = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`)
    .call(xAxis);
  
  svg.append('defs')
    .append('clipPath')
    .attr('id', 'chart-clip')
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height);
  
  const cells = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`)
    .attr('clip-path', 'url(#chart-clip)')
    .attr('width', width)
    .style('cursor', 'pointer');
  
  const computeWidth = (d, scale) => {
    const startStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
    const start = new Date(startStr);
    const end = d.end ? new Date(d.end.endsWith('Z') ? d.end : d.end + 'Z') : new Date(start.getTime() + HOUR);
    const w = scale(end) - scale(start);
    return w > cellPad ? w - cellPad : cellPad;
  };
  
  // CHANGED: Append tooltip to body instead of container, and add unique class
  const tooltipClass = 'timeline-tooltip-' + Date.now();
  const tooltip = d3.select('body')
    .append('div')
    .attr('class', tooltipClass)
    .style('position', 'absolute')
    .style('display', 'none')
    .style('background', '#000')
    .style('color', '#fff')
    .style('padding', '8px 12px')
    .style('border-radius', '4px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('z-index', 1000)
    .style('line-height', '1.4');
  
  cells.selectAll()
    .data(chartData)
    .enter()
    .append('rect')
    .attr('class', 'cell')
    .attr('x', (d) => {
      const timestampStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
      return x(new Date(timestampStr));
    })
    .attr('y', (d) => y(d.row) + cellPad)
    .attr('width', (d) => computeWidth(d, x))
    .attr('height', y.bandwidth() - 2)
    .attr('fill', (d) => {
      const deviceType = deviceTypes[d.row];
      return getColorForValue(d.value, null, deviceType, timeWindow);
    })
    .on('mouseenter', function(evt, d) {
      d3.select(this)
        .attr('opacity', 0.7)
        .attr('stroke', '#444')
        .attr('stroke-width', 1);
      
      const timestampStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
      const date = new Date(timestampStr);
      const dateStr = formatTooltipDate(date, timeWindow, timezone);
      
      tooltip.html(
        `<strong>${d.row}</strong><br>` +
        `${dateStr}<br>` +
        `${d.value} records`
      )
      .style('display', 'block')
      .style('left', `${evt.pageX + 10}px`)
      .style('top', `${evt.pageY - 40}px`);
    })
    .on('mousemove', function(evt) {
      tooltip
        .style('left', `${evt.pageX + 10}px`)
        .style('top', `${evt.pageY - 40}px`);
    })
    .on('mouseleave', function() {
      d3.select(this)
        .attr('opacity', 1.0)
        .attr('stroke', null);
      tooltip.style('display', 'none');
    })
    .on('click', (evt, d) => {
      if (onCellClick) onCellClick(d, locationName);
    });
  
  const yAxisGroup = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);
  
  yLabels.forEach((label) => {
    yAxisGroup.append('text')
      .attr('x', -5)
      .attr('y', y(label) + y.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('class', 'y-label')
      .style('font-size', '12px')
      .style('fill', '#0369a1')
      .style('cursor', 'pointer')
      .text(label)
      .on('click', function(evt) {
        const deviceId = deviceIdMap[label];
        if (onLabelClick && deviceId) {
          onLabelClick(evt, deviceId, locationName);
        }
      });
  });
  
  const zoom = d3.zoom()
    .filter((evt) => evt.type === 'wheel' ? evt.ctrlKey : true)
    .scaleExtent([0.2, 30])
    .translateExtent([[0, 0], [width, height]])
    .on('zoom', zoomed);
  
  svg.call(zoom);
  
  function zoomed(e) {
    const newScale = e.transform.rescaleX(x);
    cells.selectAll('.cell')
      .attr('x', (d) => {
        const timestampStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
        return newScale(new Date(timestampStr));
      })
      .attr('width', (d) => computeWidth(d, newScale));
    gX.call(xAxis.scale(newScale));
  }
  
  const controlsParent = d3.select(container).node().parentNode.parentNode;
  const ctrls = d3.select(controlsParent);
  
  ctrls.select('.pan-left').on('click', () => pan('left'));
  ctrls.select('.pan-right').on('click', () => pan('right'));
  ctrls.select('.zoom-in').on('click', () => zoomTo('in'));
  ctrls.select('.zoom-out').on('click', () => zoomTo('out'));
  ctrls.select('.reset').on('click', () => reset());
  
  function pan(dir) {
    const amount = dir === 'right' ? panAmount : -panAmount;
    const t = d3.zoomTransform(svg.node());
    const transform = d3.zoomIdentity.translate(t.x - amount, t.y).scale(t.k);
    zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), transform);
  }
  
  function zoomTo(dir) {
    const center = width / 2;
    const zoomTransform = d3.zoomTransform(svg.node());
    const scale = zoomTransform.k;
    const extent = zoom.scaleExtent();
    let xPos = zoomTransform.x;
    const factor = dir === 'in' ? 1.5 : 1 / 1.5;
    let targetScale = scale * factor;
    
    if (targetScale < extent[0] || targetScale > extent[1]) {
      targetScale = Math.max(extent[0], Math.min(extent[1], targetScale));
    }
    
    const actualFactor = targetScale / scale;
    xPos = (xPos - center) * actualFactor + center;
    
    const transform = d3.zoomIdentity.translate(xPos, zoomTransform.y).scale(targetScale);
    zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), transform);
  }
  
  function reset() {
    svg.transition().duration(500).ease(d3.easeLinear).call(zoom.transform, d3.zoomIdentity);
  }
  
  // ADDED: Return cleanup function to remove tooltip
  return () => {
    d3.select('.' + tooltipClass).remove();
  };
}

export default function LastXDays({ location }) {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  
  const [timeWindow, setTimeWindow] = useState('hourly');
  const [daysBack, setDaysBack] = useState('-2d');
  const [containerWidth, setContainerWidth] = useState(800);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceTypes, setDeviceTypes] = useState({});
  const [allDeviceNames, setAllDeviceNames] = useState([]);
  const [deviceIdMap, setDeviceIdMap] = useState({});
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');
  
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  const daysLabel = {
    '-2d': '2',
    '-7d': '7',
    '-30d': '30',
    '-90d': '90',
    '-1y': '365',
    'showAll': 'All'
  }[daysBack] || '2';
  
  const handleCellClick = (data, locationName) => {
    const cellStart = new Date(data.timestamp);
    
    let cellEnd;
    if (data.end) {
      cellEnd = new Date(data.end);
    } else {
      cellEnd = new Date(cellStart.getTime() + (timeWindow === 'daily' ? DAY : HOUR));
    }
    
    navigate(`/query-browser?location=${locationName}&device_id=${data.meta.deviceId}&start=${cellStart.toISOString()}&end=${cellEnd.toISOString()}&timezone=${timezone}`);
  };
  
  const handleLabelClick = (evt, deviceId, locationName) => {
    const params = new URLSearchParams({
      location: locationName,
      device_id: deviceId,
      start: daysBack,
      page: '0'
    });
    
    const url = `/query-browser?${params.toString()}`;
    
    if (evt.button === 1 || evt.ctrlKey || evt.metaKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };
  
  const handleDaysBackChange = (e, val) => {
    if (val) {
      setDaysBack(val);
      if (val === 'showAll' && timeWindow === 'hourly') {
        setTimeWindow('daily');
      }
    }
  };
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    let timeoutId;
    const resizeObserver = new ResizeObserver(entries => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width);
        }
      }, 100);
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);
  
  useEffect(() => {
    if (!location) return;
    
    // Set loading immediately and clear data
    setLoading(true);
    setTimelineData(null);
    
    const deviceNames = location.devices.map(device => device.name);
    setAllDeviceNames(deviceNames);
    
    const idMap = {};
    location.devices.forEach(device => {
      idMap[device.name] = device.serialNumber;
    });
    setDeviceIdMap(idMap);
    
    const promises = location.devices.map(device => {
      return queryData({
        start: daysBack,
        timezone: "UTC",
        filter: {
          location: location.name,
          device_id: device.serialNumber
        }
      }).then(records => ({
        deviceName: device.name,
        deviceId: device.serialNumber,
        deviceType: device.type,
        records: records
      }));
    });
    
    Promise.all(promises)
      .then(results => {
        const cellUnit = timeWindow === 'daily' ? DAY : HOUR;
        const data = {};
        const types = {};
        
        results.forEach(({ deviceName, deviceId, deviceType, records }) => {
          types[deviceName] = deviceType;
          const buckets = {};
          
          records.forEach(record => {
            const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
            const timestamp = new Date(timestampStr).getTime();
            const bucketTime = Math.floor(timestamp / cellUnit) * cellUnit;
            
            if (!buckets[bucketTime]) {
              buckets[bucketTime] = {
                timestamp: new Date(bucketTime).toISOString(),
                end: new Date(bucketTime + cellUnit).toISOString(),
                value: 0,
                meta: { deviceId }
              };
            }
            buckets[bucketTime].value += 1;
          });
          
          data[deviceName] = Object.values(buckets).sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          );
        });
        
        setTimelineData(data);
        setDeviceTypes(types);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch timeline data:", err);
        setLoading(false);
      });
  }, [location, daysBack, timeWindow, timezone]);
  
  useEffect(() => {
    if (!chartRef.current) return;
    
    // Always clear any existing chart first
    d3.select(chartRef.current).selectAll('*').remove();
    
    // Only draw if we have data
    if (!timelineData) return;
    
    const width = containerWidth - margin.left - margin.right;
    const cleanup = drawTimeline(
      chartRef.current, 
      timelineData, 
      width, 
      handleCellClick, 
      timeWindow, 
      deviceTypes, 
      location.name, 
      allDeviceNames, 
      timezone,
      deviceIdMap,
      handleLabelClick
    );
    
    // Clean up tooltip when component unmounts or before redrawing
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineData, containerWidth, timeWindow, deviceTypes, allDeviceNames, timezone, deviceIdMap]);
  
  return (
    <div className="mt-4">
      <div className="border rounded-md bg-white" style={{ overflow: 'hidden' }}>
        <div className="px-4 py-2 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Last {daysLabel} days of data</h2>
            <div className="flex items-center gap-3">
              <ToggleButtonGroup
                value={timeWindow}
                exclusive
                onChange={(e, val) => val && setTimeWindow(val)}
                size="small"
                aria-label="change time windows"
                style={{ height: '29px' }}
              >
                <ToggleButton value="hourly" aria-label="hourly" style={{ height: '29px' }}>
                  hourly
                </ToggleButton>
                <ToggleButton value="daily" aria-label="daily" style={{ height: '29px' }}>
                  daily
                </ToggleButton>
              </ToggleButtonGroup>
              
              <ToggleButtonGroup
                value={daysBack}
                exclusive
                onChange={handleDaysBackChange}
                size="small"
                aria-label="change last x days"
                style={{ height: '29px' }}
              >
                <ToggleButton value="showAll" aria-label={quickRangeLabels['showAll']} style={{ height: '29px' }}>
                  <span style={{ fontSize: '1.1em', paddingBottom: '1px' }}>-∞</span>
                  {timeWindow === 'hourly' && daysBack !== 'showAll' ? ' (daily)' : ''}
                </ToggleButton>
                <ToggleButton value="-1y" aria-label={quickRangeLabels['-1y']} style={{ height: '29px' }}>
                  -1y
                </ToggleButton>
                <ToggleButton value="-90d" aria-label={quickRangeLabels['-90d']} style={{ height: '29px' }}>
                  -90d
                </ToggleButton>
                <ToggleButton value="-30d" aria-label={quickRangeLabels['-30d']} style={{ height: '29px' }}>
                  -30d
                </ToggleButton>
                <ToggleButton value="-7d" aria-label={quickRangeLabels['-7d']} style={{ height: '29px' }}>
                  -7d
                </ToggleButton>
                <ToggleButton value="-2d" aria-label={quickRangeLabels['-2d']} style={{ height: '29px' }}>
                  -2d
                </ToggleButton>
              </ToggleButtonGroup>
              
              <div className="flex items-center gap-1 border rounded" style={{ height: '29px' }}>
                <IconButton size="small" className="reset" title="reset zoom/panning" style={{ height: '27px', width: '27px' }}>
                  <HomeIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="pan-left" title="pan left" style={{ height: '27px', width: '27px' }}>
                  <ArrowLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" className="zoom-in" title="zoom in" style={{ height: '27px', width: '27px' }}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="zoom-out" title="zoom out" style={{ height: '27px', width: '27px' }}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="pan-right" title="pan right" style={{ height: '27px', width: '27px' }}>
                  <ArrowRight fontSize="small" />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-4 pt-4 pb-0" ref={containerRef} style={{ minHeight: '200px', position: 'relative' }}>
          {/* Loading overlay */}
          {loading && (
            <div 
              className="flex items-center justify-center py-12" 
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'white', 
                zIndex: 10 
              }}
            >
              <CircularProgress size={24} />
              <span className="ml-3 text-gray-500">Loading timeline...</span>
            </div>
          )}
          
          {/* Chart container - always rendered */}
          <div ref={chartRef} className="w-full" style={{ minHeight: 0, lineHeight: 0 }}></div>
        </div>
      </div>
    </div>
  );
}